/**
 * Campaigns Dashboard
 * Complete campaign management system for admin to send platform-wide campaigns
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Send, Plus, Edit, Trash2, Search, Users, Calendar, Mail,
  Target, Tag as TagIcon, X, Save, Play, TestTube, Clock,
  CheckCircle, AlertCircle, TrendingUp, Eye, Copy
} from 'lucide-react';
import './CampaignsDashboard.css';

function CampaignsDashboard({ apiWithAccessControl }) {
  const [campaigns, setCampaigns] = useState([]);
  const [segments, setSegments] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [showBuilder, setShowBuilder] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showTestModal, setShowTestModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailCampaign, setDetailCampaign] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);

  const showStatus = useCallback((type, text) => {
    setStatusMessage({ type, text });
    setTimeout(() => setStatusMessage(null), 3000);
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadCampaigns(),
        loadSegments(),
        loadTags()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    try {
      const response = await apiWithAccessControl('/admin/campaigns');
      const data = await response.json();
      if (data.success) {
        setCampaigns(data.campaigns || []);
      }
    } catch (error) {
      console.error('Error loading campaigns:', error);
    }
  };

  const loadSegments = async () => {
    try {
      const response = await apiWithAccessControl('/admin/segments');
      const data = await response.json();
      if (data.success) {
        setSegments(data.segments || []);
      }
    } catch (error) {
      console.error('Error loading segments:', error);
    }
  };

  const loadTags = async () => {
    try {
      const response = await apiWithAccessControl('/admin/tags');
      const data = await response.json();
      if (data.success) {
        setTags(data.tags || []);
      }
    } catch (error) {
      console.error('Error loading tags:', error);
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;

    try {
      const response = await apiWithAccessControl(`/admin/campaigns/${campaignId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        showStatus('success', 'Campaign deleted successfully');
        loadCampaigns();
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
      showStatus('error', 'Failed to delete campaign');
    }
  };

  const sendCampaign = async (campaignId) => {
    try {
      const response = await apiWithAccessControl(`/admin/campaigns/${campaignId}/send`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        showStatus('success', `Campaign sent successfully to ${data.stats?.recipientCount || 0} recipients`);
        setShowSendModal(false);
        loadCampaigns();
      } else {
        showStatus('error', data.message || 'Failed to send campaign');
      }
    } catch (error) {
      console.error('Error sending campaign:', error);
      showStatus('error', 'Failed to send campaign');
    }
  };

  const sendTestEmail = async (campaignId) => {
    if (!testEmail) {
      showStatus('error', 'Please enter a test email address');
      return;
    }

    try {
      const response = await apiWithAccessControl(`/admin/campaigns/${campaignId}/test`, {
        method: 'POST',
        body: JSON.stringify({ testEmail })
      });
      const data = await response.json();
      if (data.success) {
        showStatus('success', `Test email sent to ${testEmail}`);
        setShowTestModal(false);
        setTestEmail('');
      } else {
        showStatus('error', data.message || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      showStatus('error', 'Failed to send test email');
    }
  };

  const duplicateCampaign = async (campaign) => {
    try {
      const duplicateData = {
        name: `${campaign.name} (Copy)`,
        subject: campaign.subject || '',
        content: campaign.content || '',
        description: campaign.description || '',
        audience: campaign.audience || 'ALL_MERCHANTS',
        type: campaign.type || 'EMAIL',
        tags: campaign.tags || [],
        segmentId: campaign.segmentId || ''
      };

      const response = await apiWithAccessControl('/admin/campaigns', {
        method: 'POST',
        body: JSON.stringify(duplicateData)
      });

      const data = await response.json();
      if (data.success) {
        showStatus('success', `Campaign duplicated as "${duplicateData.name}"`);
        loadCampaigns();
      } else {
        showStatus('error', data.message || 'Failed to duplicate campaign');
      }
    } catch (error) {
      console.error('Error duplicating campaign:', error);
      showStatus('error', 'Failed to duplicate campaign');
    }
  };

  const viewCampaignDetail = (campaign) => {
    setDetailCampaign(campaign);
    setShowDetailModal(true);
  };

  const createCampaign = () => {
    setSelectedCampaign(null);
    setShowBuilder(true);
  };

  const editCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setShowBuilder(true);
  };

  // Filter campaigns by tab
  const filteredCampaigns = campaigns.filter(campaign => {
    // Filter by tab
    if (activeTab !== 'all') {
      const status = campaign.status?.toLowerCase() || 'draft';
      if (activeTab !== status) return false;
    }

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        campaign.name?.toLowerCase().includes(query) ||
        campaign.subject?.toLowerCase().includes(query) ||
        campaign.description?.toLowerCase().includes(query)
      );
    }

    return true;
  });

  // Calculate stats
  const stats = {
    total: campaigns.length,
    draft: campaigns.filter(c => c.status === 'DRAFT').length,
    sent: campaigns.filter(c => c.status === 'SENT').length,
    scheduled: campaigns.filter(c => c.status === 'SCHEDULED').length,
    totalRecipients: campaigns.reduce((sum, c) => sum + (c.recipientCount || 0), 0)
  };

  // Campaign Builder Component
  const CampaignBuilder = () => {
    const [campaignData, setCampaignData] = useState(selectedCampaign || {
      name: '',
      subject: '',
      content: '',
      description: '',
      audience: 'ALL_MERCHANTS',
      type: 'EMAIL',
      segmentId: '',
      tags: [],
      scheduledAt: ''
    });

    const AUDIENCE_OPTIONS = [
      { value: 'ALL_MERCHANTS', label: 'All Merchants', description: 'Send to all merchants on the platform' },
      { value: 'SEGMENT', label: 'Specific Segment', description: 'Target merchants in a specific segment' },
      { value: 'TAGGED', label: 'Tagged Merchants', description: 'Send to merchants with specific tags' }
    ];

    const TYPE_OPTIONS = [
      { value: 'EMAIL', label: 'Email Campaign', description: 'Send via email' },
      { value: 'IN_APP', label: 'In-App Notification', description: 'Show in merchant dashboard' },
      { value: 'BOTH', label: 'Email + In-App', description: 'Send both email and notification' }
    ];

    const VARIABLES = [
      { key: 'merchantName', label: 'Merchant Name', example: 'John\'s Store' },
      { key: 'firstName', label: 'First Name', example: 'John' },
      { key: 'lastName', label: 'Last Name', example: 'Doe' },
      { key: 'email', label: 'Email', example: 'john@example.com' },
      { key: 'storeName', label: 'Store Name', example: 'My Shop' }
    ];

    const insertVariable = (variable) => {
      const textarea = document.querySelector('textarea[name="content"]');
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = campaignData.content || '';
        const before = text.substring(0, start);
        const after = text.substring(end);
        const newText = before + `{{${variable}}}` + after;
        setCampaignData({ ...campaignData, content: newText });

        // Set cursor position after inserted variable
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + variable.length + 4, start + variable.length + 4);
        }, 0);
      }
    };

    const saveCampaign = async () => {
      if (!campaignData.name?.trim()) {
        showStatus('error', 'Please enter a campaign name');
        return;
      }

      if (!campaignData.subject?.trim()) {
        showStatus('error', 'Please enter a subject line');
        return;
      }

      if (!campaignData.content?.trim()) {
        showStatus('error', 'Please enter campaign content');
        return;
      }

      try {
        const url = selectedCampaign
          ? `/admin/campaigns/${selectedCampaign.id}`
          : '/admin/campaigns';
        const method = selectedCampaign ? 'PUT' : 'POST';

        const response = await apiWithAccessControl(url, {
          method,
          body: JSON.stringify(campaignData)
        });

        const data = await response.json();
        if (data.success) {
          showStatus('success', data.message || 'Campaign saved successfully');
          setShowBuilder(false);
          loadCampaigns();
        } else {
          showStatus('error', data.message || 'Failed to save campaign');
        }
      } catch (error) {
        console.error('Error saving campaign:', error);
        showStatus('error', 'Failed to save campaign');
      }
    };

    return (
      <div className="mkt-camp-builder">
        <div className="mkt-camp-builder-header">
          <h2>{selectedCampaign ? 'Edit Campaign' : 'Create New Campaign'}</h2>
          <button className="mkt-camp-btn-icon" onClick={() => setShowBuilder(false)}>
            <X size={20} />
          </button>
        </div>

        <div className="mkt-camp-builder-body">
          {/* Basic Information */}
          <div className="mkt-camp-builder-section">
            <h3><Mail size={20} /> Campaign Information</h3>
            <p className="mkt-camp-section-desc">Basic details about your campaign</p>

            <div className="mkt-camp-form-group">
              <label>Campaign Name *</label>
              <input
                type="text"
                value={campaignData.name || ''}
                onChange={(e) => setCampaignData({ ...campaignData, name: e.target.value })}
                placeholder="e.g., Product Feature Announcement"
              />
              <small>Internal name to identify this campaign</small>
            </div>

            <div className="mkt-camp-form-group">
              <label>Description</label>
              <textarea
                value={campaignData.description || ''}
                onChange={(e) => setCampaignData({ ...campaignData, description: e.target.value })}
                placeholder="Brief description of campaign purpose..."
                rows={2}
              />
            </div>
          </div>

          {/* Audience Selection */}
          <div className="mkt-camp-builder-section">
            <h3><Target size={20} /> Target Audience</h3>
            <p className="mkt-camp-section-desc">Who should receive this campaign?</p>

            <div className="mkt-camp-form-group">
              <label>Audience Type *</label>
              <select
                value={campaignData.audience || 'ALL_MERCHANTS'}
                onChange={(e) => setCampaignData({ ...campaignData, audience: e.target.value })}
              >
                {AUDIENCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label} - {option.description}
                  </option>
                ))}
              </select>
            </div>

            {campaignData.audience === 'SEGMENT' && (
              <div className="mkt-camp-form-group">
                <label>Select Segment *</label>
                <select
                  value={campaignData.segmentId || ''}
                  onChange={(e) => setCampaignData({ ...campaignData, segmentId: e.target.value })}
                >
                  <option value="">Choose a segment...</option>
                  {segments.map((segment) => (
                    <option key={segment.id} value={segment.id}>
                      {segment.name} ({segment.merchantCount || 0} merchants)
                    </option>
                  ))}
                </select>
              </div>
            )}

            {campaignData.audience === 'TAGGED' && (
              <div className="mkt-camp-form-group">
                <label>Select Tags *</label>
                <select
                  multiple
                  value={campaignData.tags || []}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                    setCampaignData({ ...campaignData, tags: selected });
                  }}
                  style={{ minHeight: '120px' }}
                >
                  {tags.map((tag) => (
                    <option key={tag.tag || tag.name} value={tag.tag || tag.name}>
                      {tag.tag || tag.name} ({tag.type || 'user'})
                    </option>
                  ))}
                </select>
                <small>Hold Ctrl/Cmd to select multiple tags</small>
              </div>
            )}
          </div>

          {/* Campaign Type */}
          <div className="mkt-camp-builder-section">
            <h3><Send size={20} /> Delivery Method</h3>
            <p className="mkt-camp-section-desc">How should this campaign be delivered?</p>

            <div className="mkt-camp-form-group">
              <label>Campaign Type *</label>
              <select
                value={campaignData.type || 'EMAIL'}
                onChange={(e) => setCampaignData({ ...campaignData, type: e.target.value })}
              >
                {TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label} - {option.description}
                  </option>
                ))}
              </select>
            </div>

            <div className="mkt-camp-form-group">
              <label>Schedule Send (Optional)</label>
              <input
                type="datetime-local"
                value={campaignData.scheduledAt || ''}
                onChange={(e) => setCampaignData({ ...campaignData, scheduledAt: e.target.value })}
              />
              <small>Leave empty to save as draft</small>
            </div>
          </div>

          {/* Email Content */}
          <div className="mkt-camp-builder-section">
            <h3><Mail size={20} /> Email Content</h3>
            <p className="mkt-camp-section-desc">Compose your campaign message</p>

            <div className="mkt-camp-form-group">
              <label>Subject Line *</label>
              <input
                type="text"
                value={campaignData.subject || ''}
                onChange={(e) => setCampaignData({ ...campaignData, subject: e.target.value })}
                placeholder="e.g., Exciting New Features Just for You!"
              />
            </div>

            <div className="mkt-camp-form-group">
              <label>Message Content *</label>
              <textarea
                name="content"
                value={campaignData.content || ''}
                onChange={(e) => setCampaignData({ ...campaignData, content: e.target.value })}
                placeholder="Write your message here... Use {{variables}} for personalization"
                rows={10}
              />
              <small>Use variables below to personalize your message</small>
            </div>

            {/* Variable Tags */}
            <div className="mkt-camp-form-group">
              <label>Personalization Variables</label>
              <p style={{ fontSize: '12px', color: '#666', margin: '4px 0 8px 0' }}>
                Click to insert into your message
              </p>
              <div className="mkt-camp-variable-tags">
                {VARIABLES.map((variable) => (
                  <button
                    key={variable.key}
                    className="mkt-camp-variable-tag"
                    onClick={() => insertVariable(variable.key)}
                    type="button"
                    title={`Example: ${variable.example}`}
                  >
                    <Copy size={12} />
                    {`{{${variable.key}}}`}
                  </button>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="mkt-camp-preview-box">
              <h4>Preview</h4>
              <div className="mkt-camp-preview-content">
                {campaignData.content || 'Your message will appear here...'}
              </div>
            </div>
          </div>
        </div>

        <div className="mkt-camp-builder-footer">
          <div>
            {selectedCampaign && selectedCampaign.status === 'DRAFT' && (
              <button
                className="mkt-camp-btn-secondary"
                onClick={() => {
                  setSelectedCampaign(selectedCampaign);
                  setShowTestModal(true);
                }}
              >
                <TestTube size={16} />
                Send Test
              </button>
            )}
          </div>
          <div className="mkt-camp-footer-actions">
            <button className="mkt-camp-btn-secondary" onClick={() => setShowBuilder(false)}>
              Cancel
            </button>
            <button className="mkt-camp-btn-primary" onClick={saveCampaign}>
              <Save size={16} />
              {selectedCampaign ? 'Update Campaign' : 'Save Campaign'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Test Email Modal
  const TestEmailModal = () => (
    <div className="mkt-camp-modal-overlay" onClick={() => setShowTestModal(false)}>
      <div className="mkt-camp-modal small" onClick={(e) => e.stopPropagation()}>
        <div className="mkt-camp-test-email">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3>Send Test Email</h3>
            <button className="mkt-camp-btn-icon" onClick={() => setShowTestModal(false)}>
              <X size={20} />
            </button>
          </div>

          <div className="mkt-camp-form-group">
            <label>Test Email Address</label>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="your-email@example.com"
            />
            <small>We'll send a test version of this campaign to this email</small>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
            <button className="mkt-camp-btn-secondary" onClick={() => setShowTestModal(false)}>
              Cancel
            </button>
            <button
              className="mkt-camp-btn-primary"
              onClick={() => sendTestEmail(selectedCampaign?.id)}
            >
              <Send size={16} />
              Send Test
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Send Confirmation Modal
  const SendConfirmationModal = () => {
    const campaign = selectedCampaign;
    if (!campaign) return null;

    return (
      <div className="mkt-camp-modal-overlay" onClick={() => setShowSendModal(false)}>
        <div className="mkt-camp-modal small" onClick={(e) => e.stopPropagation()}>
          <div className="mkt-camp-send-confirm">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3>Send Campaign</h3>
              <button className="mkt-camp-btn-icon" onClick={() => setShowSendModal(false)}>
                <X size={20} />
              </button>
            </div>

            <p>
              Are you sure you want to send <strong>{campaign.name}</strong>?
            </p>

            <div className="mkt-camp-confirm-details">
              <p><strong>Subject:</strong> {campaign.subject}</p>
              <p><strong>Audience:</strong> {campaign.audience}</p>
              <p><strong>Type:</strong> {campaign.type}</p>
              {campaign.segmentId && (
                <p><strong>Segment:</strong> {segments.find(s => s.id === campaign.segmentId)?.name || 'Unknown'}</p>
              )}
            </div>

            <p style={{ fontSize: '14px', color: '#dc2626' }}>
              This action cannot be undone. The campaign will be sent immediately.
            </p>

            <div className="mkt-camp-confirm-actions">
              <button className="mkt-camp-btn-secondary" onClick={() => setShowSendModal(false)}>
                Cancel
              </button>
              <button
                className="mkt-camp-btn-success"
                onClick={() => sendCampaign(campaign.id)}
              >
                <Send size={16} />
                Send Campaign
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Campaign Detail / Analytics Modal
  const CampaignDetailModal = () => {
    const campaign = detailCampaign;
    if (!campaign) return null;

    const campaignStats = campaign.stats || {};
    const sentCount = campaignStats.sent || 0;
    const openedCount = campaignStats.opened || 0;
    const clickedCount = campaignStats.clicked || 0;
    const openRate = campaign.openRate || 0;
    const clickRate = campaign.clickRate || 0;

    return (
      <div className="mkt-camp-modal-overlay" onClick={() => setShowDetailModal(false)}>
        <div className="mkt-camp-modal large" onClick={(e) => e.stopPropagation()}>
          <div className="mkt-camp-detail">
            <div className="mkt-camp-detail-header">
              <div>
                <h2>{campaign.name}</h2>
                <span className={`mkt-camp-status ${campaign.status?.toLowerCase() || 'draft'}`}>
                  {campaign.status || 'DRAFT'}
                </span>
              </div>
              <button className="mkt-camp-btn-icon" onClick={() => setShowDetailModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="mkt-camp-detail-body">
              {/* Performance Metrics */}
              <div className="mkt-camp-detail-section">
                <h3><TrendingUp size={18} /> Performance Metrics</h3>
                <div className="mkt-camp-detail-metrics">
                  <div className="mkt-camp-metric-card">
                    <div className="mkt-camp-metric-icon mkt-camp-metric-sent">
                      <Send size={20} />
                    </div>
                    <div className="mkt-camp-metric-value">{sentCount.toLocaleString()}</div>
                    <div className="mkt-camp-metric-label">Total Sent</div>
                  </div>
                  <div className="mkt-camp-metric-card">
                    <div className="mkt-camp-metric-icon mkt-camp-metric-opened">
                      <Mail size={20} />
                    </div>
                    <div className="mkt-camp-metric-value">{openedCount.toLocaleString()}</div>
                    <div className="mkt-camp-metric-label">Opens</div>
                  </div>
                  <div className="mkt-camp-metric-card">
                    <div className="mkt-camp-metric-icon mkt-camp-metric-clicked">
                      <Eye size={20} />
                    </div>
                    <div className="mkt-camp-metric-value">{clickedCount.toLocaleString()}</div>
                    <div className="mkt-camp-metric-label">Clicks</div>
                  </div>
                  <div className="mkt-camp-metric-card">
                    <div className="mkt-camp-metric-icon mkt-camp-metric-openrate">
                      <TrendingUp size={20} />
                    </div>
                    <div className="mkt-camp-metric-value">{openRate}%</div>
                    <div className="mkt-camp-metric-label">Open Rate</div>
                  </div>
                  <div className="mkt-camp-metric-card">
                    <div className="mkt-camp-metric-icon mkt-camp-metric-clickrate">
                      <Target size={20} />
                    </div>
                    <div className="mkt-camp-metric-value">{clickRate}%</div>
                    <div className="mkt-camp-metric-label">Click Rate</div>
                  </div>
                </div>
              </div>

              {/* Campaign Info */}
              <div className="mkt-camp-detail-section">
                <h3><Mail size={18} /> Campaign Details</h3>
                <div className="mkt-camp-detail-info-grid">
                  <div className="mkt-camp-detail-info-item">
                    <span className="mkt-camp-detail-info-label">Subject</span>
                    <span className="mkt-camp-detail-info-value">{campaign.subject}</span>
                  </div>
                  <div className="mkt-camp-detail-info-item">
                    <span className="mkt-camp-detail-info-label">Type</span>
                    <span className="mkt-camp-detail-info-value">{campaign.type || 'EMAIL'}</span>
                  </div>
                  <div className="mkt-camp-detail-info-item">
                    <span className="mkt-camp-detail-info-label">Audience</span>
                    <span className="mkt-camp-detail-info-value">{campaign.audience || 'ALL_MERCHANTS'}</span>
                  </div>
                  {campaign.sentAt && (
                    <div className="mkt-camp-detail-info-item">
                      <span className="mkt-camp-detail-info-label">Sent Date</span>
                      <span className="mkt-camp-detail-info-value">
                        {new Date(campaign.sentAt).toLocaleString()}
                      </span>
                    </div>
                  )}
                  {campaign.recipientCount > 0 && (
                    <div className="mkt-camp-detail-info-item">
                      <span className="mkt-camp-detail-info-label">Recipients</span>
                      <span className="mkt-camp-detail-info-value">
                        {campaign.recipientCount.toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Content Preview */}
              {campaign.content && (
                <div className="mkt-camp-detail-section">
                  <h3><Edit size={18} /> Content</h3>
                  <div className="mkt-camp-detail-content-preview">
                    {campaign.content}
                  </div>
                </div>
              )}
            </div>

            <div className="mkt-camp-detail-footer">
              <button className="mkt-camp-btn-secondary" onClick={() => setShowDetailModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mkt-camp-dashboard">
      {/* Status Message Banner */}
      {statusMessage && (
        <div className={`mkt-camp-status-banner mkt-camp-status-${statusMessage.type}`}>
          <div className="mkt-camp-status-banner-content">
            {statusMessage.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
            <span>{statusMessage.text}</span>
          </div>
          <button className="mkt-camp-btn-icon" onClick={() => setStatusMessage(null)}>
            <X size={16} />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="mkt-camp-header">
        <div>
          <h1>Email Campaigns</h1>
          <p>Create and manage platform-wide marketing campaigns</p>
        </div>
        <button className="mkt-camp-btn-primary" onClick={createCampaign}>
          <Plus size={18} />
          Create Campaign
        </button>
      </div>

      {/* Stats */}
      <div className="mkt-camp-stats">
        <div className="mkt-camp-stat-card">
          <h3>{stats.total}</h3>
          <p>Total Campaigns</p>
        </div>
        <div className="mkt-camp-stat-card">
          <h3>{stats.sent}</h3>
          <p>Sent</p>
        </div>
        <div className="mkt-camp-stat-card">
          <h3>{stats.draft}</h3>
          <p>Drafts</p>
        </div>
        <div className="mkt-camp-stat-card">
          <h3>{stats.totalRecipients.toLocaleString()}</h3>
          <p>Total Recipients</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mkt-camp-tabs">
        <button
          className={`mkt-camp-tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All ({stats.total})
        </button>
        <button
          className={`mkt-camp-tab ${activeTab === 'draft' ? 'active' : ''}`}
          onClick={() => setActiveTab('draft')}
        >
          Drafts ({stats.draft})
        </button>
        <button
          className={`mkt-camp-tab ${activeTab === 'sent' ? 'active' : ''}`}
          onClick={() => setActiveTab('sent')}
        >
          Sent ({stats.sent})
        </button>
        <button
          className={`mkt-camp-tab ${activeTab === 'scheduled' ? 'active' : ''}`}
          onClick={() => setActiveTab('scheduled')}
        >
          Scheduled ({stats.scheduled})
        </button>
      </div>

      {/* Search and Filters */}
      <div className="mkt-camp-controls">
        <div className="mkt-camp-search">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search campaigns..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Campaigns List */}
      {loading ? (
        <div className="mkt-camp-loading">Loading campaigns...</div>
      ) : filteredCampaigns.length === 0 ? (
        <div className="mkt-camp-empty">
          <Send size={64} />
          <h3>No campaigns yet</h3>
          <p>Create your first campaign to engage with merchants</p>
          <button className="mkt-camp-btn-primary" onClick={createCampaign}>
            <Plus size={18} />
            Create First Campaign
          </button>
        </div>
      ) : (
        <div className="mkt-camp-grid">
          {filteredCampaigns.map((campaign) => (
            <div key={campaign.id} className="mkt-camp-card">
              <div className="mkt-camp-card-header">
                <div className="mkt-camp-title">
                  <h3>{campaign.name}</h3>
                  <span className={`mkt-camp-status ${campaign.status?.toLowerCase() || 'draft'}`}>
                    {campaign.status || 'DRAFT'}
                  </span>
                </div>
                <div className="mkt-camp-actions">
                  {campaign.status === 'SENT' && (
                    <button
                      className="mkt-camp-btn-icon"
                      onClick={() => viewCampaignDetail(campaign)}
                      title="View Analytics"
                    >
                      <Eye size={16} />
                    </button>
                  )}
                  {campaign.status === 'DRAFT' && (
                    <>
                      <button
                        className="mkt-camp-btn-icon"
                        onClick={() => {
                          setSelectedCampaign(campaign);
                          setShowTestModal(true);
                        }}
                        title="Send Test"
                      >
                        <TestTube size={16} />
                      </button>
                      <button
                        className="mkt-camp-btn-icon"
                        onClick={() => {
                          setSelectedCampaign(campaign);
                          setShowSendModal(true);
                        }}
                        title="Send Campaign"
                      >
                        <Play size={16} />
                      </button>
                    </>
                  )}
                  <button
                    className="mkt-camp-btn-icon"
                    onClick={() => duplicateCampaign(campaign)}
                    title="Duplicate Campaign"
                  >
                    <Copy size={16} />
                  </button>
                  <button
                    className="mkt-camp-btn-icon"
                    onClick={() => editCampaign(campaign)}
                    title="Edit"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    className="mkt-camp-btn-icon danger"
                    onClick={() => deleteCampaign(campaign.id)}
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {campaign.description && (
                <p className="mkt-camp-desc">{campaign.description}</p>
              )}

              <div className="mkt-camp-meta">
                <div className="mkt-camp-meta-item">
                  <Mail size={14} />
                  {campaign.type || 'EMAIL'}
                </div>
                <div className="mkt-camp-meta-item">
                  <Target size={14} />
                  {campaign.audience || 'ALL_MERCHANTS'}
                </div>
                {campaign.sentAt && (
                  <div className="mkt-camp-meta-item">
                    <Calendar size={14} />
                    {new Date(campaign.sentAt).toLocaleDateString()}
                  </div>
                )}
                {campaign.recipientCount > 0 && (
                  <div className="mkt-camp-meta-item">
                    <Users size={14} />
                    {campaign.recipientCount} recipients
                  </div>
                )}
              </div>

              <div style={{ marginTop: '12px', fontSize: '14px', color: '#333' }}>
                <strong>Subject:</strong> {campaign.subject}
              </div>

              {/* Performance Stats for Sent Campaigns */}
              {campaign.status === 'SENT' && campaign.stats && (
                <div className="mkt-camp-card-perf">
                  <div className="mkt-camp-card-perf-item">
                    <Send size={13} />
                    <span className="mkt-camp-card-perf-value">{(campaign.stats.sent || 0).toLocaleString()}</span>
                    <span className="mkt-camp-card-perf-label">Sent</span>
                  </div>
                  <div className="mkt-camp-card-perf-item">
                    <TrendingUp size={13} />
                    <span className="mkt-camp-card-perf-value">{campaign.openRate || 0}%</span>
                    <span className="mkt-camp-card-perf-label">Open Rate</span>
                  </div>
                  <div className="mkt-camp-card-perf-item">
                    <Target size={13} />
                    <span className="mkt-camp-card-perf-value">{campaign.clickRate || 0}%</span>
                    <span className="mkt-camp-card-perf-label">Click Rate</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Campaign Builder Modal */}
      {showBuilder && (
        <div className="mkt-camp-modal-overlay">
          <div className="mkt-camp-modal large">
            <CampaignBuilder />
          </div>
        </div>
      )}

      {/* Test Email Modal */}
      {showTestModal && <TestEmailModal />}

      {/* Send Confirmation Modal */}
      {showSendModal && <SendConfirmationModal />}

      {/* Campaign Detail Modal */}
      {showDetailModal && <CampaignDetailModal />}
    </div>
  );
}

export default CampaignsDashboard;
